import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CounterpartyUser,
  CounterpartyUserMutationResult,
  CreateCounterpartyUserParams,
  CurrentCounterpartyUser,
  ChangeCounterpartyUserStatusParams,
  GetCounterpartyUsersParams,
  SendCounterpartyUserActivationRequestParams,
  UpdateCounterpartyUserParams,
} from '@/features/counterpartyUsers/types';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export const counterpartyUsersApi = createApi({
  reducerPath: 'counterpartyUsersApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/representatives',
  }),
  tagTypes: ['CounterpartyUser', 'CounterpartyUsers'],
  endpoints: builder => ({
    getCurrentCounterpartyUser: builder.query<CurrentCounterpartyUser, void>({
      query: () => ({
        url: '/current',
      }),
      providesTags: ['CounterpartyUser'],
    }),
    getCounterpartyUsers: builder.query<
      CounterpartyUser[],
      GetCounterpartyUsersParams
    >({
      query: params => ({
        url: '',
        params,
      }),
      providesTags: ['CounterpartyUsers'],
    }),
    createCounterpartyUser: builder.mutation<
      CounterpartyUserMutationResult,
      CreateCounterpartyUserParams
    >({
      query: ({ organizationId, ...data }) => ({
        url: '',
        method: 'POST',
        params: { organizationId },
        data,
      }),
      invalidatesTags: ['CounterpartyUsers'],
    }),
    updateCounterpartyUser: builder.mutation<
      CounterpartyUserMutationResult,
      UpdateCounterpartyUserParams
    >({
      query: ({ id, organizationId, ...data }) => ({
        url: `/${id}`,
        method: 'PUT',
        params: { organizationId },
        data,
      }),
      invalidatesTags: ['CounterpartyUsers'],
    }),
    deactivateCounterpartyUser: builder.mutation<
      void,
      ChangeCounterpartyUserStatusParams
    >({
      query: ({ id, organizationId }) => ({
        url: `/${id}/deactivate`,
        method: 'POST',
        params: { organizationId },
      }),
      invalidatesTags: ['CounterpartyUsers'],
    }),
    restoreCounterpartyUser: builder.mutation<
      void,
      ChangeCounterpartyUserStatusParams
    >({
      query: ({ id, organizationId }) => ({
        url: `/${id}/restore`,
        method: 'POST',
        params: { organizationId },
      }),
      invalidatesTags: ['CounterpartyUsers'],
    }),
    sendActivationRequest: builder.mutation<
      void,
      SendCounterpartyUserActivationRequestParams
    >({
      query: ({ id, organizationId }) => ({
        url: `/${id}/send-activation-request`,
        method: 'POST',
        params: { organizationId },
      }),
      invalidatesTags: ['CounterpartyUsers'],
    }),
  }),
});

export const {
  useGetCurrentCounterpartyUserQuery,
  useLazyGetCurrentCounterpartyUserQuery,
  useGetCounterpartyUsersQuery,
  useCreateCounterpartyUserMutation,
  useUpdateCounterpartyUserMutation,
  useDeactivateCounterpartyUserMutation,
  useRestoreCounterpartyUserMutation,
  useSendActivationRequestMutation,
} = counterpartyUsersApi;
